import { Cron } from '../api'
import { TransfersV2 } from '../api-v2'
import { mapV1DebitTransferRequestToV2DebitTransferRequestBody, mapV1ExpediteTransferRequestToV2ExpediteTransferRequestBody } from '../api-v2/mappings/map-transfers'
import { ApiVersionDelagator } from "./api-delegator"
/**
 * @typedef {import('../api-v2/js-doc').V1ExpediteTransferRequest} V1ExpediteTransferRequest
 * @typedef {import('../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 * @typedef {import('../api-v2/js-doc').V1DebitTransferRequest} V1DebitTransferRequest
 * @typedef {import('../api-v2/js-doc').V2DebitTransferRequest} V2DebitTransferRequest
 *
 */

export class TransfersApiDelegator extends ApiVersionDelagator {
  /**
   * @param {string} clientId - ID of the client
   * @param {V1ExpediteTransferRequest} v1Request - V1 expedite transfer request
   * @returns {Promise<any>}
   */
  async initiateExpediteTransfer (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#initiateExpediteTransferV1,
      this.#initiateExpediteTransferV2,
      {
        operationName: 'initiate-expedite-transfer',
      }
    )
  }

  /**
   * @param {string} clientId - ID of the client
   * @param {V1DebitTransferRequest} v1Request - V1 debit transfer request
   * @returns {Promise<any>}
   */
  async initiateDebitRegularTransfer (clientId, v1Request) {
    return await this.runWriteApiCalls(
      clientId,
      v1Request,
      this.#initiateDebitTransferV1,
      this.#initiateDebitTransferV2.bind(null, clientId),
      {
        operationName: 'initiate-debit-transfer',
      }
    )
  }

  /**
   * @returns {Promise<any>}
   */
  async initiateRegularTransfer() {
    return await this.runWriteApiCalls(
      null,
      {},
      this.#initiateRegulartransferV1,
      this.#initiateRegulartransferV2,
      {
        operationName: 'initiate-regular-transfer',
      }
    )
  }

  /**
   * @param {V1ExpediteTransferRequest} v1Request - V1 expedite transfer request
   * @returns {Promise<any>}
   */
  async #initiateExpediteTransferV1 (v1Request) {
    return Cron.runClientWire(v1Request)
  }

  /**
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1ExpediteTransferRequest} v1Request - V1 expedite transfer request
   * @returns {Promise<any>}
   */
  async #initiateExpediteTransferV2 (v2MappingOptions, v1Request) {
    const v2Request = mapV1ExpediteTransferRequestToV2ExpediteTransferRequestBody(v2MappingOptions, v1Request)
    return await new TransfersV2().initiateExpediteTransfer(v2Request)
  }


  /**
   * @param {import('../api-v2/js-doc').V1DebitTransferRequest} v1Request - V1 debit transfer request
   * @returns {Promise<any>}
   */
  async #initiateDebitTransferV1 (v1Request) {
    return Cron.debitBank(v1Request)
  }

  /**
   * @param {string} clientId - client ID
   * @param {V2MappingOptions} v2MappingOptions - V2 mapping options
   * @param {V1DebitTransferRequest} v1Request - V1 expedite transfer request
   * @returns {Promise<any>}
   */
  async #initiateDebitTransferV2 (clientId, v2MappingOptions, v1Request) {
    const v2Request = mapV1DebitTransferRequestToV2DebitTransferRequestBody(v2MappingOptions, v1Request, clientId)
    return await new TransfersV2().initiateDebitTransfer(v2Request)
  }

  /**
   * @returns {Promise<any>}
   */
  async #initiateRegulartransferV1 () {
    return Cron.runNachaJob()
  }

  /**
   * @returns {Promise<any>}
   */
  async #initiateRegulartransferV2 () {
    const payload = {
      __ingestThrough: true
    }
    return await new TransfersV2().initiateRegularTransfer(payload)
  }
}
